import React, { useState, useEffect } from "react";
import { HiOutlineChartPie } from "react-icons/hi";
import { Select } from "flowbite-react";
import { formatCurrency } from "../../../utils/formatCurrency";
import { useSpendingAnalytics } from "../hooks/useSpendingAnalytics";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  DATE_RANGE_TO_LABEL,
  DATE_RANGES,
  DateRange,
} from "../utils/dateUtils";

export const SpendingAnalytics: React.FC = () => {
  const [timeframe, setTimeframe] = useState<DateRange>(DATE_RANGES.MONTH);
  const [isExpanded, setIsExpanded] = useState(false);
  const { data } = useSpendingAnalytics(timeframe);

  const { categoryData, trendData, topMerchants } = data || {
    categoryData: {
      labels: [],
      datasets: [{ data: [], backgroundColor: [] }],
    },
    trendData: [],
    topMerchants: [],
  };

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isExpanded]);

  return (
    <>
      {isExpanded && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-all duration-300" />
      )}
      <div
        className={`bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm ${
          isExpanded
            ? "fixed inset-0 z-50 m-4 scale-100 opacity-100 transition-all duration-300 flex flex-col"
            : ""
        }`}
        style={
          isExpanded
            ? { height: "calc(100% - 2rem)", width: "calc(100% - 2rem)" }
            : {}
        }
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <HiOutlineChartPie className="text-indigo-500" />
            Spending Analysis
          </h2>

          <div className="flex items-center gap-2">
            <Select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value as DateRange)}
              className="w-36"
            >
              <option value={DATE_RANGES.WEEK}>
                {DATE_RANGE_TO_LABEL[DATE_RANGES.WEEK]}
              </option>
              <option value={DATE_RANGES.MONTH}>
                {DATE_RANGE_TO_LABEL[DATE_RANGES.MONTH]}
              </option>
              <option value={DATE_RANGES.YEAR}>
                {DATE_RANGE_TO_LABEL[DATE_RANGES.YEAR]}
              </option>
            </Select>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 12H4"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 12h16M12 4v16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div
          className={`grid gap-6 ${isExpanded ? "grid-cols-1 h-[calc(100vh-10rem)]" : "grid-cols-1 lg:grid-cols-2"}`}
        >
          <div style={{ height: isExpanded ? "calc(33vh)" : "300px" }}>
            <h3 className="text-sm font-medium mb-4">Category Distribution</h3>
            <div className="h-[calc(100%-2rem)]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData?.labels.map((label, index) => ({
                      name: label,
                      value: categoryData?.datasets[0].data[index],
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={isExpanded ? 80 : 60}
                    outerRadius={isExpanded ? 120 : 80}
                    dataKey="value"
                    nameKey="name"
                  >
                    {categoryData?.datasets[0].backgroundColor.map(
                      (color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ),
                    )}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(value as number)}
                  />
                  <Legend
                    layout="horizontal"
                    align="center"
                    verticalAlign="bottom"
                    height={36}
                    wrapperStyle={{ display: isExpanded ? "block" : "none" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{ height: isExpanded ? "calc(33vh)" : "300px" }}>
            <h3 className="text-sm font-medium mb-4">Spending Trend</h3>
            <div className="h-[calc(100%-2rem)]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => formatCurrency(value as number)}
                  />
                  <Legend
                    wrapperStyle={{ display: isExpanded ? "block" : "none" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="expenses"
                    stroke="rgba(239, 68, 68, 0.8)"
                    dot={true}
                  />
                  <Line
                    type="monotone"
                    dataKey="income"
                    stroke="rgba(34, 197, 94, 0.8)"
                    dot={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={isExpanded ? "col-span-1" : "lg:col-span-2"}>
            <h3 className="text-sm font-medium mb-4">Top Merchants</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {topMerchants.map((merchant, index) => (
                <MerchantCard key={index} {...merchant} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

interface MerchantCardProps {
  name: string;
  amount: number;
  transactionCount: number;
  trend: number;
}

const MerchantCard: React.FC<MerchantCardProps> = ({
  name,
  amount,
  transactionCount,
  trend,
}) => (
  <div className="p-4 rounded-lg bg-gray-50 dark:bg-slate-700/50">
    <div className="font-medium mb-2">{name}</div>
    <div className="text-lg font-semibold mb-1">{formatCurrency(amount)}</div>
    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
      <span>{transactionCount} transactions</span>
      <span className={trend >= 0 ? "text-green-600" : "text-red-600"}>
        {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}%
      </span>
    </div>
  </div>
);
